import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import type { UseSignUpParams } from '~/domains/auth/components/SignUpSteps/types';
import { BRANDS, EO_BRANDS } from '~/domains/auth/consts';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { useRecaptcha } from '~/domains/auth/hooks/useRecaptcha';
import type {
  AuthResponse,
  AuthSuccessResponse,
} from '~/services/identity/unauthenticated/identity.service.api';
import { signUp } from '~/services/identity/unauthenticated/identity.service.api';
import { useShowSplashNotification } from '~/domains/common/hooks/useShowSplashNotification';

import {
  resolveAuthErrorMessage,
  type AuthErrorResponseBody,
  type AuthMutationError,
} from '../utils/resolveAuthErrorMessage';

export type SignUpResult =
  | { mfaRequired: false; response: AuthSuccessResponse }
  | { mfaRequired: true; verificationToken: string; canResendIn?: number };

const useSignUp = ({
  formValues,
  onSignUpSuccess,
}: UseSignUpParams & {
  onSignUpSuccess?: (result: SignUpResult) => void;
}) => {
  const {
    email,
    password,
    firstName,
    lastName,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _referralCodeValue,
  } = formValues;
  const { brand, platform } = useGetBranding();
  const destination = brand in EO_BRANDS ? EO_BRANDS[brand] : undefined;
  const { showSplashNotification } = useShowSplashNotification();

  const { setIsSignUpInitialised } = useContext(UserContext);
  const router = useRouter();

  const [errorCode, setErrorCode] = useState<string>();
  const [errorStatus, setErrorStatus] = useState<number>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const { getRecaptchaToken } = useRecaptcha();

  const { mutate: handleSignUp, isLoading: isSigningUp } = useMutation({
    mutationKey: ['signUp'],
    mutationFn: async () => {
      setErrorCode(undefined);
      setErrorStatus(undefined);
      setErrorMessage(undefined);

      // Start blocking the SignUp redirect (preventing redirect race condition)
      setIsSignUpInitialised(true);

      const token = await getRecaptchaToken();
      return signUp({
        email,
        password,
        referral: router.query.referral as string,
        token,
        referralCode: _referralCodeValue,
        destination,
        platform,
        ...(brand === BRANDS.RYP ? { firstName, lastName } : {}),
      });
    },
    onSuccess: (data: AuthResponse) => {
      if (data.status === 'SUCCESS') {
        onSignUpSuccess?.({ mfaRequired: false, response: data });
      } else {
        onSignUpSuccess?.({
          mfaRequired: true,
          verificationToken: data.verificationToken,
          canResendIn: data.canResendIn,
        });
      }
    },
    onError: (error: AuthMutationError) => {
      const response = axios.isAxiosError<AuthErrorResponseBody>(error)
        ? error.response
        : undefined;
      const errorMessageResponse = response?.data?.message;
      const errorStatusResponse = response?.status;
      const errorCodeResponse = response?.data?.error;
      const showSupportCTA = response?.data?.showSupportCTA;
      setErrorCode(errorCodeResponse);
      setErrorStatus(errorStatusResponse);
      setErrorMessage(errorMessageResponse);

      // Reset the signup initialised ref
      setIsSignUpInitialised(false);

      const notificationMessage = resolveAuthErrorMessage(
        error,
        'There was an error creating your account'
      );

      showSplashNotification({
        title: 'Sign Up Error',
        message: notificationMessage,
        color: 'red',
        autoClose: 5000,
        showSupportCTA,
      });
    },
  });

  return {
    handleSignUp,
    isSigningUp,

    errorCode,
    errorStatus,
    errorMessage,
  };
};

export default useSignUp;
